import { renderMarkdown } from '../markdown/markdown';

import './styles/detail.css';

// The read side of a record, which this panel did not have.
//
// Until now "opening" a tier or a task meant opening its edit form, so the
// route was guarded by `tiers:write` / `tasks:write` and anybody who could only
// READ the work could not look at it. The backend never conflated the two —
// `breakdown:read` and `tasks:read` exist and are held by roles that hold no
// write permission — so the panel was the only place where reading cost a
// write permission.
//
// These are the pieces that reading needs and a form does not: a value that can
// be absent without being an error, a list that is a list, and a place to say
// "this is frozen" without drawing a disabled input nobody can use.

export function Detail({ children }) {
    return <dl className="detail">{children}</dl>;
}

// `empty` is what an absent value reads as. It is a parameter because the right
// word changes: a description that nobody wrote is "Sin descripción", and a
// tier with no open questions is a milestone, not a gap.
//
// `markdown` renders a written text (a description, an expected outcome) the way
// it reads, instead of showing its `#` and `**`. Only for strings: a value that
// is already an element is shown as it is.
export function Field({ label, value, empty = 'Sin registrar', wide, mono, markdown }) {
    const missing = value == null || (typeof value === 'string' ? !value.trim() : value === '');
    const rich = markdown && !missing && typeof value === 'string';

    return (
        <div className={`detail__row ${wide ? 'detail__row--wide' : ''}`}>
            <dt className="detail__label">{label}</dt>
            <dd className={`detail__value ${missing ? 'detail__value--empty' : ''} ${mono ? 'detail__value--mono' : ''} ${rich ? 'detail__value--rich' : ''}`}>
                {missing
                    ? empty
                    : rich
                        ? <div className="md md--field">{renderMarkdown(value)}</div>
                        : value}
            </dd>
        </div>
    );
}

// A list shown as a list. The forms keep one item per line in a textarea
// because that is how a person writes them; reading them back as a blob of
// text with newlines is where "three acceptance criteria" turns into "a
// paragraph".
export function FieldList({ label, items, empty = 'Ninguno', wide = true, ordered }) {
    const rows = Array.isArray(items) ? items.filter(Boolean) : [];
    const List = ordered ? 'ol' : 'ul';

    return (
        <div className={`detail__row ${wide ? 'detail__row--wide' : ''}`}>
            <dt className="detail__label">{label}</dt>
            <dd className="detail__value">
                {rows.length === 0
                    ? <span className="detail__value--empty">{empty}</span>
                    : (
                        <List className="detail__list">
                            {rows.map((item, index) => (
                                <li key={index} className="detail__item">{item}</li>
                            ))}
                        </List>
                    )}
            </dd>
        </div>
    );
}

// A state reads with a word and a shape, never with colour alone: the panel has
// two themes and some readers have neither.
export function Tag({ children, tone = 'neutral', title }) {
    return (
        <span className={`detail-tag detail-tag--${tone}`} title={title}>
            {children}
        </span>
    );
}

export function Notice({ children, tone = 'neutral' }) {
    return <p className={`detail-notice detail-notice--${tone}`}>{children}</p>;
}
